import { Mail, MapPin, Phone } from 'lucide-react';
import { cn } from '@/util/cn';
import { NextLink } from '@/components/blocks/NextLink';
import { useContactInformation } from '@/util/useContactInformation';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { Container } from '@/components/ui/container';

// Quiet "get in touch" band at the bottom of resort pages. Replaces the
// legacy full-bleed tinted band + giant 2.5rem title with a hairline-
// separated row: an eyebrow + resort name on the left, three small
// icon-led contact rows on the right. Summer mode swaps the icon tint
// and the phone / email hover from primary blue to primary-summer
// green; everything else stays neutral.

export const ContactInformation = ({
  title,
  uuid,
  isOnSummerOverview,
}: {
  title: string,
  uuid: string,
  isOnSummerOverview?: boolean
}) => {
  const { t } = useOTSTranslation('common');
  const contactInfo = useContactInformation(uuid);

  if (!contactInfo) return null;

  const { address: { city, country, postOffice }, email, phoneNumber } = contactInfo;
  const hasAddress = Boolean(postOffice || city);
  const hasPhone = Boolean(phoneNumber);
  const hasEmail = Boolean(email);

  if (!hasAddress && !hasPhone && !hasEmail) return null;

  const iconClass = cn(
    'mt-0.5 h-4 w-4 shrink-0',
    isOnSummerOverview ? 'text-primary-summer' : 'text-primary',
  );

  const linkClass = cn(
    'text-foreground/85 transition',
    isOnSummerOverview ? 'hover:text-primary-summer' : 'hover:text-secondary',
  );

  return (
    <section className="mt-12 border-t border-border">
      <Container width="xl-only" className="py-8 md:py-10">
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between md:gap-12">
          <div className="md:max-w-[20rem]">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('resort.overview.contactInfo')}
            </div>
            <h3 className="mt-1 font-heading text-2xl font-bold text-secondary">
              {title}
            </h3>
          </div>

          <div className="flex flex-col gap-4 text-sm md:flex-row md:items-start md:gap-10">
            {hasAddress && (
              <div className="flex items-start gap-2 text-muted-foreground">
                <MapPin className={iconClass} aria-hidden />
                <div className="leading-snug">
                  {postOffice && <div>{postOffice}</div>}
                  {(city || country) && (
                    <div>
                      {city}
                      {city && country ? ', ' : ''}
                      {country}
                    </div>
                  )}
                </div>
              </div>
            )}
            {(hasPhone || hasEmail) && (
              <div className="flex flex-col gap-2">
                {hasPhone && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone className={iconClass} aria-hidden />
                    <NextLink href={`tel:${phoneNumber}`} className={linkClass}>
                      {phoneNumber}
                    </NextLink>
                  </div>
                )}
                {hasEmail && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Mail className={iconClass} aria-hidden />
                    <NextLink href={`mailto:${email}`} className={linkClass}>
                      {email}
                    </NextLink>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Container>
    </section>
  );
};
